/**
 * Job Flow — standalone jobs page.
 *
 * Shows "My Work" (jobs where I'm the current owner) by default. Admins can
 * flip to "All" to see everyone's jobs. Includes a "+ New Job" dialog that
 * creates one job and lands the user on its detail page.
 *
 * Four statuses (To do · In progress · Waiting on client · Done) and four
 * actions (Pass on · Send back · Ask Michael · Waiting on client) live on
 * the job detail page.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    Plus, Settings, AlertTriangle, Search, Loader2, Flag, CheckCircle2, Clock,
    LayoutGrid, List as ListIcon, Zap, Trash2, Copy,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const JOB_TYPE_LABEL = {
    income_tax: "Income Tax Return", gst: "GST Return",
    payroll: "Payroll", others: "Others",
};
const STATUS_LABEL = {
    to_do: "To do", in_progress: "In progress",
    waiting_on_client: "Waiting on client", done: "Done",
};
const STATUS_COLOR = {
    to_do: "bg-zinc-50 text-zinc-700 border-zinc-200",
    in_progress: "bg-blue-50 text-blue-800 border-blue-200",
    waiting_on_client: "bg-amber-50 text-amber-900 border-amber-200",
    done: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

export default function JobFlow() {
    const { user } = useAuth() || {};
    const isAdmin = user?.role === "firm_admin" || user?.role === "super_admin";
    const [view, setView] = useState("list"); // list | board | stuck
    const [scope, setScope] = useState("mine"); // mine | team
    const [typeFilter, setTypeFilter] = useState("all");
    const [q, setQ] = useState("");
    const [jobs, setJobs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [settings, setSettings] = useState({ email_test_mode: true });
    const [clientsById, setClientsById] = useState({});
    const [team, setTeam] = useState([]);
    const [stuck, setStuck] = useState([]);
    const [doneJobs, setDoneJobs] = useState([]);
    const [newOpen, setNewOpen] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);

    async function reload() {
        setLoading(true);
        try {
            const effectiveScope = (view === "board" || view === "stuck") ? "team" : scope;
            const [{ data: j }, { data: s }, { data: cs }, { data: t }] = await Promise.all([
                api.get("/job-flow/jobs", { params: { scope: effectiveScope, type: typeFilter === "all" ? undefined : typeFilter, q: q || undefined } }),
                api.get("/job-flow/settings"),
                api.get("/clients"),
                api.get("/job-flow/team"),
            ]);
            setJobs(j || []);
            setSettings(s || { email_test_mode: true });
            setTeam(t || []);
            const map = {};
            for (const c of (cs || [])) map[c.id] = c;
            setClientsById(map);
            if (view === "stuck") {
                const { data: st } = await api.get("/job-flow/stuck");
                setStuck(st || []);
            }
            if (view === "board") {
                const { data: dj } = await api.get("/job-flow/jobs", { params: { scope: "team", status: "done" } });
                setDoneJobs((dj || []).slice(0, 50));
            }
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }

    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [scope, typeFilter, view]);

    const filtered = useMemo(() => {
        if (!q) return jobs;
        const qlow = q.toLowerCase();
        return jobs.filter((j) => (
            (clientsById[j.client_id]?.name || "").toLowerCase().includes(qlow)
            || (j.title || "").toLowerCase().includes(qlow)
            || (j.tax_year || "").toLowerCase().includes(qlow)
        ));
    }, [q, jobs, clientsById]);

    return (
        <div className="space-y-4" data-testid="job-flow">
            <header className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <div className="flex items-center gap-2">
                        <h1 className="text-xl font-semibold text-[#0F1115]">Job Flow</h1>
                    </div>
                    <p className="text-[12.5px] text-[#5E636E]">Income Tax · GST · Payroll · Others — handoff with a note, pass between staff and admins.</p>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="jf-add-several">
                        <Copy className="h-3.5 w-3.5 mr-1" />Add several
                    </Button>
                    <Button variant="outline" asChild data-testid="jf-settings">
                        <Link to="/app/job-flow/settings"><Settings className="h-3.5 w-3.5 mr-1" />Settings</Link>
                    </Button>
                    <Button onClick={() => setNewOpen(true)} className="bg-[#0F1115] text-white hover:bg-[#2E2E2E]" data-testid="jf-new-job">
                        <Plus className="h-3.5 w-3.5 mr-1" />New job
                    </Button>
                </div>
            </header>

            {/* View switcher — available to all firm users (admin + staff) */}
            <div className="inline-flex rounded-md border border-[#E4E4E7] overflow-hidden" data-testid="jf-view-switch">
                    {[
                        { k: "list", label: "List", icon: ListIcon },
                        { k: "board", label: "Team board", icon: LayoutGrid },
                        { k: "stuck", label: "Stuck", icon: Zap },
                    ].map((v, i) => (
                        <button key={v.k} onClick={() => setView(v.k)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-[12.5px] ${view === v.k ? "bg-[#0F1115] text-white" : "bg-white text-[#5E636E] hover:text-[#0F1115]"} ${i > 0 ? "border-l border-[#E4E4E7]" : ""}`}
                            data-testid={`jf-view-${v.k}`}>
                            <v.icon className="h-3.5 w-3.5" />{v.label}
                            {v.k === "stuck" && stuck.length > 0 && <span className="inline-flex items-center justify-center rounded-full bg-red-100 text-red-700 h-4 min-w-[18px] px-1 text-[10px] font-bold">{stuck.length}</span>}
                        </button>
                    ))}
                </div>

            <div className="flex flex-wrap items-center gap-2 border-b border-[#E4E4E7] pb-2">
                {isAdmin && view === "list" && (
                    <>
                        <TabBtn active={scope === "mine"} onClick={() => setScope("mine")} testid="jf-scope-mine">My work</TabBtn>
                        <TabBtn active={scope === "team"} onClick={() => setScope("team")} testid="jf-scope-team">All (team)</TabBtn>
                        <span className="w-px h-5 bg-[#E4E4E7] mx-1" />
                    </>
                )}
                {["all", "income_tax", "gst", "payroll", "others"].map((t) => (
                    <TabBtn key={t} active={typeFilter === t} onClick={() => setTypeFilter(t)} testid={`jf-type-${t}`}>
                        {t === "all" ? "All types" : JOB_TYPE_LABEL[t]}
                    </TabBtn>
                ))}
                <div className="relative ml-auto">
                    <Search className="h-3.5 w-3.5 absolute left-3 top-2.5 text-[#5E636E]" />
                    <Input placeholder="Search client / title…" value={q} onChange={(e) => setQ(e.target.value)}
                        className="pl-8 h-8 text-[13px] w-56" data-testid="jf-search" />
                </div>
            </div>

            {loading ? (
                <div className="py-10 text-center text-[#5E636E]"><Loader2 className="h-4 w-4 animate-spin inline" /> Loading…</div>
            ) : view === "board" ? (
                <TeamBoard jobs={filtered} doneJobs={doneJobs} team={team} clientsById={clientsById} onChanged={reload} />
            ) : view === "stuck" ? (
                <StuckList jobs={stuck.filter((j) => typeFilter === "all" || j.type === typeFilter)} clientsById={clientsById} />
            ) : filtered.length === 0 ? (
                <EmptyState onNew={() => setNewOpen(true)} />
            ) : (
                <div className="grid gap-2">
                    {filtered.map((j) => <JobRow key={j.id} job={j} client={clientsById[j.client_id]} />)}
                </div>
            )}

            {newOpen && <NewJobDialog open={newOpen} onClose={() => setNewOpen(false)} onCreated={() => { setNewOpen(false); reload(); }} />}
            {bulkOpen && <AddSeveralDialog open={bulkOpen} onClose={() => setBulkOpen(false)} onDone={() => { setBulkOpen(false); reload(); }} />}
        </div>
    );
}

function TabBtn({ active, children, onClick, testid }) {
    return (
        <button onClick={onClick} data-testid={testid}
            className={`text-[12.5px] px-2.5 h-7 rounded-full border ${active ? "bg-[#0F1115] text-white border-[#0F1115]" : "bg-white text-[#5E636E] border-[#E4E4E7] hover:border-[#0F1115] hover:text-[#0F1115]"}`}>
            {children}
        </button>
    );
}

function JobRow({ job, client }) {
    const overdue = job.due_date && job.status !== "done" && new Date(job.due_date) < new Date();
    const title = job.type === "others" ? (job.title || "Untitled") : (JOB_TYPE_LABEL[job.type] || job.type);
    const period = job.type === "income_tax" ? job.tax_year : (job.period_start && job.period_end ? `${job.period_start} → ${job.period_end}` : "");
    return (
        <Link to={`/app/job-flow/${job.id}`} className="flex items-center gap-3 rounded-md border border-[#E4E4E7] bg-white px-3 py-2 hover:border-[#0F1115]" data-testid={`jf-row-${job.id}`}>
            <TypeChip type={job.type} />
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <div className="font-medium text-[13.5px] text-[#0F1115] truncate">{client?.name || (job.type === "others" ? "Internal" : "—")}</div>
                    {job.needs_michael && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-50 border border-red-200 text-red-700 text-[10.5px] font-semibold px-1.5 py-0.5" data-testid={`jf-needs-michael-${job.id}`}>
                            <Flag className="h-3 w-3" /> Needs Michael
                        </span>
                    )}
                </div>
                <div className="text-[11.5px] text-[#5E636E] truncate">{title}{period ? ` · ${period}` : ""}</div>
            </div>
            {job.due_date && (
                <div className={`text-[11.5px] ${overdue ? "text-red-700 font-semibold" : "text-[#5E636E]"}`}>
                    <Clock className="h-3 w-3 inline" /> {job.due_date}
                </div>
            )}
            <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10.5px] font-semibold ${STATUS_COLOR[job.status] || ""}`}>
                {STATUS_LABEL[job.status] || job.status}
            </span>
        </Link>
    );
}

function TypeChip({ type }) {
    const map = {
        income_tax: { label: "Income tax", cls: "bg-indigo-50 text-indigo-800 border-indigo-200" },
        gst: { label: "GST", cls: "bg-emerald-50 text-emerald-800 border-emerald-200" },
        payroll: { label: "Payroll", cls: "bg-amber-50 text-amber-800 border-amber-200" },
        others: { label: "Others", cls: "bg-zinc-100 text-zinc-700 border-zinc-200" },
    }[type] || { label: type, cls: "bg-zinc-100 text-zinc-700 border-zinc-200" };
    return <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10.5px] font-semibold ${map.cls}`}>{map.label}</span>;
}

function EmptyState({ onNew }) {
    return (
        <div className="rounded-md border border-dashed border-[#E4E4E7] bg-white p-8 text-center">
            <CheckCircle2 className="h-6 w-6 text-[#5E636E] mx-auto mb-2" />
            <div className="text-[13px] font-medium text-[#0F1115]">No jobs yet</div>
            <div className="text-[12px] text-[#5E636E] mt-1">Create a job and hand it to someone on the team.</div>
            <Button onClick={onNew} className="mt-3 bg-[#0F1115] text-white hover:bg-[#2E2E2E]" data-testid="jf-empty-new">
                <Plus className="h-3.5 w-3.5 mr-1" />New job
            </Button>
        </div>
    );
}

function currentNzFy(d = new Date()) {
    // NZ FY ends 31 March; FY2027 = period ending 31 Mar 2027.
    const y = d.getFullYear();
    return d.getMonth() + 1 >= 4 ? y + 1 : y;
}

function fyOptions() {
    const cur = currentNzFy();
    const years = [];
    for (let y = cur - 3; y <= cur + 2; y++) years.push(`FY${y}`);
    return years;
}

// dd.mm.yyyy ↔ yyyy-mm-dd (native date input format)
function nzToIso(s) {
    if (!s) return "";
    const m = String(s).match(/^(\d{2})[./-](\d{2})[./-](\d{4})$/);
    if (!m) return "";
    return `${m[3]}-${m[2]}-${m[1]}`;
}
function isoToNz(s) {
    if (!s) return "";
    const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return "";
    return `${m[3]}.${m[2]}.${m[1]}`;
}

function DateField({ value, onChange, testid, placeholder = "dd.mm.yyyy" }) {
    return (
        <Input
            type="date"
            value={nzToIso(value)}
            onChange={(e) => onChange(isoToNz(e.target.value))}
            placeholder={placeholder}
            className="mt-1 h-8 text-[12.5px]"
            data-testid={testid}
        />
    );
}

function NewJobDialog({ open, onClose, onCreated }) {
    const navigate = useNavigate();
    const [type, setType] = useState("income_tax");
    const [clients, setClients] = useState([]);
    const [clientFilter, setClientFilter] = useState("");
    const [clientId, setClientId] = useState("");
    const [internal, setInternal] = useState(false); // others only
    const [title, setTitle] = useState("");
    const [periodStart, setPeriodStart] = useState("");
    const [periodEnd, setPeriodEnd] = useState("");
    const [taxYear, setTaxYear] = useState(`FY${currentNzFy()}`);
    const [dueDate, setDueDate] = useState("");
    const [requestedBy, setRequestedBy] = useState("");
    const [pipeline, setPipeline] = useState([]);
    const [stepIdx, setStepIdx] = useState(0);
    const [team, setTeam] = useState([]);
    const [ownerId, setOwnerId] = useState("");
    const [firstNote, setFirstNote] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/clients").then(({ data }) => setClients(data || [])).catch(() => {});
        api.get("/job-flow/team").then(({ data }) => setTeam(data || [])).catch(() => {});
    }, []);

    useEffect(() => {
        api.get("/job-flow/pipelines").then(({ data }) => {
            const p = (data || []).find((x) => x.type === type);
            setPipeline(p?.steps || []);
            setStepIdx(0);
            setOwnerId(p?.steps?.[0]?.default_owner_user_id || "");
        }).catch(() => {});
    }, [type]);

    useEffect(() => {
        const def = pipeline[stepIdx]?.default_owner_user_id || "";
        if (def) setOwnerId(def);
    }, [stepIdx, pipeline]);

    const filteredClients = useMemo(() => {
        if (!clientFilter) return clients.slice(0, 50);
        const q = clientFilter.toLowerCase();
        return clients.filter((c) => (c.name || "").toLowerCase().includes(q)).slice(0, 50);
    }, [clientFilter, clients]);

    async function submit() {
        if (type === "others" && !title.trim()) { toast.error("Title required"); return; }
        if (type !== "others" && !clientId) { toast.error("Pick a client"); return; }
        if (!ownerId) { toast.error("Pick an owner"); return; }
        setSaving(true);
        try {
            const body = {
                type,
                client_id: internal && type === "others" ? null : (clientId || null),
                title: type === "others" ? title.trim() : null,
                period_start: ["gst", "payroll"].includes(type) ? (periodStart || null) : null,
                period_end: ["gst", "payroll"].includes(type) ? (periodEnd || null) : null,
                tax_year: type === "income_tax" ? (taxYear || null) : null,
                due_date: dueDate || null,
                requested_by: type === "others" ? (requestedBy || null) : null,
                starting_step_index: stepIdx,
                starting_owner_user_id: ownerId,
                first_note: firstNote || null,
            };
            const { data } = await api.post("/job-flow/jobs", body);
            toast.success("Job created");
            onCreated();
            if (data?.id) navigate(`/app/job-flow/${data.id}`);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    }

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-2xl" data-testid="jf-new-dialog">
                <DialogHeader><DialogTitle>New job</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div>
                        <Label>Job type</Label>
                        <div className="flex gap-2 mt-1">
                            {Object.entries(JOB_TYPE_LABEL).map(([k, label]) => (
                                <button key={k} onClick={() => setType(k)} data-testid={`jf-new-type-${k}`}
                                    className={`text-[12.5px] px-2.5 h-8 rounded-md border ${type === k ? "bg-[#0F1115] text-white border-[#0F1115]" : "bg-white text-[#5E636E] border-[#E4E4E7]"}`}>
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {type === "others" && (
                        <>
                            <div>
                                <Label>Title *</Label>
                                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Letter to IRD"
                                    className="mt-1 h-8 text-[12.5px]" data-testid="jf-new-title" />
                            </div>
                            <label className="flex items-center gap-2 text-[12.5px]">
                                <input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} data-testid="jf-new-internal" />
                                Internal / no client
                            </label>
                        </>
                    )}

                    {!(type === "others" && internal) && (
                        <div>
                            <Label>Client{type === "others" ? " (optional)" : " *"}</Label>
                            <Input placeholder="Search client…" value={clientFilter} onChange={(e) => setClientFilter(e.target.value)}
                                className="mt-1 h-8 text-[12.5px]" data-testid="jf-new-client-search" />
                            <div className="mt-1 max-h-40 overflow-y-auto rounded-md border border-[#E4E4E7] bg-white divide-y divide-[#EEEDE7]">
                                {filteredClients.map((c) => (
                                    <label key={c.id} className="flex items-center gap-2 px-3 py-1.5 text-[12.5px] cursor-pointer hover:bg-zinc-50">
                                        <input type="radio" name="jf-client" checked={clientId === c.id} onChange={() => setClientId(c.id)} data-testid={`jf-new-client-${c.id}`} />
                                        <span>{c.name}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}

                    {type === "income_tax" && (
                        <div>
                            <Label>Tax year</Label>
                            <select value={taxYear} onChange={(e) => setTaxYear(e.target.value)}
                                className="mt-1 w-full h-8 rounded-md border border-[#E4E4E7] bg-white px-2 text-[12.5px]" data-testid="jf-new-tax-year">
                                {fyOptions().map((y) => <option key={y} value={y}>{y}</option>)}
                            </select>
                        </div>
                    )}
                    {["gst", "payroll"].includes(type) && (
                        <div className="grid grid-cols-2 gap-2">
                            <div><Label>Period start</Label><DateField value={periodStart} onChange={setPeriodStart} testid="jf-new-period-start" /></div>
                            <div><Label>Period end</Label><DateField value={periodEnd} onChange={setPeriodEnd} testid="jf-new-period-end" /></div>
                        </div>
                    )}

                    <div className="grid grid-cols-2 gap-2">
                        <div><Label>Due date{type === "others" ? " (optional)" : ""}</Label><DateField value={dueDate} onChange={setDueDate} testid="jf-new-due" /></div>
                        {type === "others" && (
                            <div><Label>Requested by</Label><Input value={requestedBy} onChange={(e) => setRequestedBy(e.target.value)} className="mt-1 h-8 text-[12.5px]" data-testid="jf-new-requested-by" /></div>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <Label>Starting step</Label>
                            <select value={stepIdx} onChange={(e) => setStepIdx(Number(e.target.value))}
                                className="mt-1 w-full h-8 rounded-md border border-[#E4E4E7] bg-white px-2 text-[12.5px]" data-testid="jf-new-step">
                                {pipeline.map((s, i) => <option key={s.key} value={i}>{i + 1}. {s.label}{s.admin_only ? " (Admin)" : ""}</option>)}
                            </select>
                        </div>
                        <div>
                            <Label>Owner *</Label>
                            <select value={ownerId} onChange={(e) => setOwnerId(e.target.value)}
                                className="mt-1 w-full h-8 rounded-md border border-[#E4E4E7] bg-white px-2 text-[12.5px]" data-testid="jf-new-owner">
                                <option value="">— Pick —</option>
                                {team.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}{u.role === "firm_admin" ? " (Admin)" : ""}</option>)}
                            </select>
                        </div>
                    </div>

                    <div>
                        <Label>First note (optional)</Label>
                        <Textarea rows={3} value={firstNote} onChange={(e) => setFirstNote(e.target.value)}
                            placeholder="A short note the next owner will see…" className="mt-1 text-[12.5px]" data-testid="jf-new-note" />
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={saving} className="bg-[#0F1115] text-white hover:bg-[#2E2E2E]" data-testid="jf-new-submit">
                        {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}Create
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


// ---------- Team board (admin only, drag-and-drop) ----------

function TeamBoard({ jobs, doneJobs = [], team, clientsById, onChanged }) {
    const { user } = useAuth() || {};
    const isAdmin = user?.role === "firm_admin" || user?.role === "super_admin";
    const [draggingId, setDraggingId] = useState(null);
    const [dropTarget, setDropTarget] = useState(null); // owner_user_id OR "ownerId:index" OR "_done"
    const [passDialog, setPassDialog] = useState(null); // { jobId, toUserId }
    const [doneDialog, setDoneDialog] = useState(null); // { job }

    const owners = useMemo(() => team.filter((u) => u.role === "firm_admin" || u.role === "firm_staff"), [team]);
    const grouped = useMemo(() => {
        const m = new Map();
        for (const u of owners) m.set(u.id, []);
        m.set("_unassigned", []);
        for (const j of jobs) {
            const k = m.has(j.current_owner_user_id) ? j.current_owner_user_id : "_unassigned";
            m.get(k).push(j);
        }
        return m;
    }, [jobs, owners]);

    function onDragStart(e, job) {
        setDraggingId(job.id);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", job.id);
    }
    async function onDropOnColumn(e, toUserId) {
        e.preventDefault();
        setDropTarget(null);
        const jobId = e.dataTransfer.getData("text/plain") || draggingId;
        setDraggingId(null);
        if (!jobId) return;
        const job = jobs.find((j) => j.id === jobId);
        if (!job) return;
        if (toUserId === "_done") {
            // Only owner (or admin) can mark done
            if (!(isAdmin || job.current_owner_user_id === user?.id)) {
                toast.error("Only the owner (or an admin) can mark this job done.");
                return;
            }
            setDoneDialog({ job });
            return;
        }
        if (job.current_owner_user_id === toUserId) {
            // Same column, dropped on empty space → move to bottom
            if (isAdmin) {
                try {
                    const colLen = (grouped.get(toUserId) || []).length;
                    await api.post(`/job-flow/jobs/${jobId}/reorder`, { to_index: colLen - 1 });
                    onChanged();
                } catch { /* ignore */ }
            }
            return;
        }
        setPassDialog({ jobId, toUserId, job });
    }
    async function onDropOnCard(e, toUserId, atIndex) {
        e.preventDefault();
        e.stopPropagation();
        setDropTarget(null);
        const jobId = e.dataTransfer.getData("text/plain") || draggingId;
        setDraggingId(null);
        if (!jobId) return;
        const job = jobs.find((j) => j.id === jobId);
        if (!job) return;
        if (job.current_owner_user_id === toUserId) {
            // Reorder within column — admin only.
            if (!isAdmin) { toast.error("Only admins can reorder priority."); return; }
            try {
                await api.post(`/job-flow/jobs/${jobId}/reorder`, { to_index: atIndex });
                onChanged();
            } catch (err) { toast.error(formatApiError(err)); }
            return;
        }
        setPassDialog({ jobId, toUserId, job });
    }

    return (
        <div className="relative" data-testid="jf-board">
            <div className="flex gap-3 pb-2 w-full">
                {owners.map((u) => {
                    const list = grouped.get(u.id) || [];
                    return (
                        <div key={u.id}
                            className={`flex-1 min-w-[200px] rounded-md border ${dropTarget === u.id ? "border-[#0F1115] bg-zinc-50" : "border-[#E4E4E7] bg-white"}`}
                            onDragOver={(e) => { e.preventDefault(); setDropTarget(u.id); }}
                            onDragLeave={() => setDropTarget((t) => t === u.id ? null : t)}
                            onDrop={(e) => onDropOnColumn(e, u.id)}
                            data-testid={`jf-col-${u.id}`}>
                            <header className="px-3 py-2 border-b border-[#EEEDE7] flex items-center justify-between">
                                <div className="font-medium text-[13px] text-[#0F1115] truncate">{u.name || u.email}{u.role === "firm_admin" ? " (Admin)" : ""}</div>
                                <span className="inline-flex items-center justify-center h-5 min-w-[22px] px-1 rounded-full bg-zinc-100 text-zinc-700 text-[11px] font-semibold">{list.length}</span>
                            </header>
                            <div className="p-2 space-y-2 min-h-[80px] max-h-[70vh] overflow-y-auto">
                                {list.length === 0 ? (
                                    <div className="text-[11.5px] text-[#5E636E] italic py-6 text-center">drop a card here</div>
                                ) : list.map((j, i) => {
                                    const canDrag = isAdmin || j.current_owner_user_id === user?.id;
                                    return (
                                        <BoardCard
                                            key={j.id}
                                            job={j}
                                            client={clientsById[j.client_id]}
                                            onDragStart={canDrag ? (e) => onDragStart(e, j) : undefined}
                                            dragging={draggingId === j.id}
                                            canReorder={isAdmin}
                                            onDropAbove={(e) => onDropOnCard(e, u.id, i)}
                                            draggable={canDrag}
                                        />
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
                {/* "Done" dumping zone — drop a card here to mark it as completed. */}
                <div
                    className={`flex-1 min-w-[200px] rounded-md border-2 ${dropTarget === "_done" ? "border-emerald-500 bg-emerald-50" : "border-dashed border-emerald-300 bg-emerald-50/40"}`}
                    onDragOver={(e) => { e.preventDefault(); setDropTarget("_done"); }}
                    onDragLeave={() => setDropTarget((t) => t === "_done" ? null : t)}
                    onDrop={(e) => onDropOnColumn(e, "_done")}
                    data-testid="jf-col-done">
                    <header className="px-3 py-2 border-b border-emerald-200 flex items-center justify-between">
                        <div className="font-medium text-[13px] text-emerald-800 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Done</div>
                        <span className="inline-flex items-center justify-center h-5 min-w-[22px] px-1 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-semibold">{doneJobs.length}</span>
                    </header>
                    <div className="p-2 space-y-2 min-h-[80px] max-h-[70vh] overflow-y-auto">
                        {doneJobs.length === 0 ? (
                            <div className="text-[11.5px] text-emerald-700 italic py-6 text-center">drop a finished card here</div>
                        ) : doneJobs.slice(0, 20).map((j) => (
                            <Link key={j.id} to={`/app/job-flow/${j.id}`}
                                className="block rounded-md border border-emerald-200 bg-white p-2 opacity-80 hover:opacity-100 hover:border-emerald-500"
                                data-testid={`jf-done-${j.id}`}>
                                <div className="flex items-center gap-1.5 mb-1">
                                    <TypeChip type={j.type} />
                                    <CheckCircle2 className="h-3 w-3 text-emerald-600" />
                                </div>
                                <div className="font-medium text-[12.5px] text-[#0F1115] truncate">{clientsById[j.client_id]?.name || (j.type === "others" ? "Internal" : "—")}</div>
                                <div className="text-[11px] text-[#5E636E] truncate">{j.type === "others" ? (j.title || "Untitled") : JOB_TYPE_LABEL[j.type]}</div>
                                {j.completed_at && (
                                    <div className="mt-1 text-[10.5px] text-emerald-700">done {new Date(j.completed_at).toLocaleDateString("en-NZ")}</div>
                                )}
                            </Link>
                        ))}
                    </div>
                </div>
            </div>
            {isAdmin && (
                <p className="mt-1 text-[11px] text-[#5E636E]">Drag cards up or down to reorder priority. Top of the column = do first.</p>
            )}

            {passDialog && (
                <BoardPassDialog
                    job={passDialog.job}
                    toUser={owners.find((u) => u.id === passDialog.toUserId)}
                    onClose={() => setPassDialog(null)}
                    onDone={() => { setPassDialog(null); onChanged(); }}
                />
            )}
            {doneDialog && (
                <MarkDoneDialog
                    job={doneDialog.job}
                    onClose={() => setDoneDialog(null)}
                    onDone={() => { setDoneDialog(null); onChanged(); }}
                />
            )}
        </div>
    );
}

function MarkDoneDialog({ job, onClose, onDone }) {
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    async function submit() {
        if (!note || note.trim().length < 5) { toast.error("Note required (5+ chars)."); return; }
        setBusy(true);
        try {
            await api.post(`/job-flow/jobs/${job.id}/mark-done`, { note });
            toast.success("Marked as done");
            onDone();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }
    return (
        <Dialog open onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md" data-testid="jf-done-dialog">
                <DialogHeader><DialogTitle>Mark job as done</DialogTitle></DialogHeader>
                <div className="text-[12.5px] text-[#5E636E]">
                    {job.type === "others" ? (job.title || "Untitled") : JOB_TYPE_LABEL[job.type]} will move to the Done column.
                </div>
                <Label className="mt-2">Closing note (required)</Label>
                <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                    className="mt-1 text-[12.5px]" placeholder="e.g. Filed with IRD, confirmation emailed." data-testid="jf-done-note" />
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="jf-done-submit">
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}Mark done
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function BoardCard({ job, client, onDragStart, dragging, canReorder, onDropAbove, draggable = true }) {
    const [over, setOver] = useState(false);
    const overdue = job.due_date && job.status !== "done" && new Date(job.due_date) < new Date();
    const title = job.type === "others" ? (job.title || "Untitled") : JOB_TYPE_LABEL[job.type];
    return (
        <div
            onDragOver={canReorder ? (e) => { e.preventDefault(); e.stopPropagation(); setOver(true); } : undefined}
            onDragLeave={canReorder ? () => setOver(false) : undefined}
            onDrop={canReorder ? (e) => { setOver(false); onDropAbove(e); } : undefined}
            className={over ? "border-t-2 border-t-[#0F1115] pt-1 -mt-1" : ""}>
            <Link to={`/app/job-flow/${job.id}`}
                draggable={draggable} onDragStart={draggable ? onDragStart : undefined}
                title={draggable ? "" : "Only the owner (or an admin) can move this card"}
                className={`block rounded-md border bg-white p-2 ${draggable ? "cursor-grab hover:border-[#0F1115]" : "cursor-pointer opacity-90"} ${dragging ? "opacity-50 border-dashed" : "border-[#E4E4E7]"}`}
                data-testid={`jf-card-${job.id}`}>
                <div className="flex items-center gap-1.5 mb-1">
                    <TypeChip type={job.type} />
                    {overdue && <span className="inline-flex h-2 w-2 rounded-full bg-red-500" title="overdue" />}
                    {job.needs_michael && <span className="text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 px-1 rounded">!M</span>}
                </div>
                <div className="font-medium text-[12.5px] text-[#0F1115] truncate">{client?.name || (job.type === "others" ? "Internal" : "—")}</div>
                <div className="text-[11px] text-[#5E636E] truncate">{title}</div>
                {job.due_date && (
                    <div className={`mt-1 text-[11px] ${overdue ? "text-red-700 font-semibold" : "text-[#5E636E]"}`}>
                        <Clock className="h-3 w-3 inline" /> {job.due_date}
                    </div>
                )}
            </Link>
        </div>
    );
}

function BoardPassDialog({ job, toUser, onClose, onDone }) {
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    async function submit() {
        if (!note || note.trim().length < 5) { toast.error("Note required (5+ chars)."); return; }
        setBusy(true);
        try {
            // Drag-and-drop reassigns at the SAME step via pass-on with to_user_id.
            // Backend pass-on advances to the next step, which is wrong for drag.
            // So we use a dedicated reassign flow: call the pass-on endpoint with the
            // same owner, but to meet the brief we treat drag as "reassign at current
            // step". We approximate using a direct patch — fall back to pass-on if the
            // server rejects.
            await api.post(`/job-flow/jobs/${job.id}/reassign`, { note, to_user_id: toUser.id });
            toast.success("Reassigned");
            onDone();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }
    return (
        <Dialog open onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-md" data-testid="jf-board-pass">
                <DialogHeader><DialogTitle>Reassign to {toUser?.name || "someone"}</DialogTitle></DialogHeader>
                <div className="text-[12.5px] text-[#5E636E]">
                    Current owner: <b className="text-[#0F1115]">—</b> (step stays the same).
                </div>
                <Label className="mt-2">Note (required)</Label>
                <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} className="mt-1 text-[12.5px]" data-testid="jf-board-pass-note" />
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="bg-[#0F1115] text-white hover:bg-[#2E2E2E]" data-testid="jf-board-pass-submit">
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}Reassign
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

// ---------- Stuck list (admin only) ----------

function StuckList({ jobs, clientsById }) {
    if (!jobs.length) {
        return (
            <div className="rounded-md border border-dashed border-[#E4E4E7] bg-white p-8 text-center">
                <CheckCircle2 className="h-6 w-6 text-emerald-600 mx-auto mb-2" />
                <div className="text-[13px] font-medium text-[#0F1115]">Nothing stuck 🎉</div>
                <div className="text-[12px] text-[#5E636E] mt-1">No overdue, idle or send-back-twice jobs.</div>
            </div>
        );
    }
    const reasonLabel = { overdue: "Overdue", no_activity_2d: "No activity 2d", sent_back_twice: "Sent back twice" };
    return (
        <div className="grid gap-2" data-testid="jf-stuck-list">
            {jobs.map((j) => (
                <Link to={`/app/job-flow/${j.id}`} key={j.id} className="flex items-center gap-3 rounded-md border border-red-200 bg-red-50/40 px-3 py-2 hover:border-red-400">
                    <TypeChip type={j.type} />
                    <div className="flex-1 min-w-0">
                        <div className="font-medium text-[13.5px] text-[#0F1115] truncate">{clientsById[j.client_id]?.name || (j.type === "others" ? "Internal" : "—")}</div>
                        <div className="text-[11.5px] text-[#5E636E] truncate">{j.type === "others" ? (j.title || "Untitled") : JOB_TYPE_LABEL[j.type]}</div>
                    </div>
                    <div className="flex flex-wrap gap-1 justify-end max-w-[260px]">
                        {j.stuck_reasons.map((r) => (
                            <span key={r} className="inline-flex items-center rounded-full border border-red-300 bg-red-100 text-red-800 text-[10.5px] font-semibold px-2 py-0.5">
                                {reasonLabel[r] || r}
                            </span>
                        ))}
                    </div>
                </Link>
            ))}
        </div>
    );
}

// ---------- Add several (bulk) ----------

function emptyBulkRow() {
    return { type: "income_tax", client_id: "", title: "", period_label: "", due_date: "", starting_step_index: 0, starting_owner_user_id: "" };
}

function AddSeveralDialog({ open, onClose, onDone }) {
    const [rows, setRows] = useState([emptyBulkRow(), emptyBulkRow(), emptyBulkRow()]);
    const [clients, setClients] = useState([]);
    const [team, setTeam] = useState([]);
    const [pipelines, setPipelines] = useState({});
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        api.get("/clients").then(({ data }) => setClients(data || [])).catch(() => {});
        api.get("/job-flow/team").then(({ data }) => setTeam(data || [])).catch(() => {});
        api.get("/job-flow/pipelines").then(({ data }) => {
            const m = {};
            for (const p of (data || [])) m[p.type] = p;
            setPipelines(m);
        }).catch(() => {});
    }, []);

    function patch(i, p) {
        setRows((xs) => xs.map((r, idx) => idx === i ? { ...r, ...p } : r));
    }
    function addRow() {
        if (rows.length >= 20) { toast.error("20 rows max per batch"); return; }
        setRows((xs) => [...xs, emptyBulkRow()]);
    }
    function removeRow(i) {
        setRows((xs) => xs.filter((_, idx) => idx !== i));
    }

    async function submit() {
        const payload = rows.map((r) => {
            const base = {
                type: r.type,
                client_id: r.client_id || null,
                title: r.type === "others" ? (r.title || null) : null,
                due_date: r.due_date || null,
                starting_step_index: Number(r.starting_step_index || 0),
                starting_owner_user_id: r.starting_owner_user_id || null,
            };
            if (r.type === "income_tax") base.tax_year = r.period_label || null;
            if (["gst", "payroll"].includes(r.type)) {
                const [s, e] = (r.period_label || "").split("→").map((x) => (x || "").trim());
                base.period_start = s || null; base.period_end = e || null;
            }
            return base;
        }).filter((p) => p.client_id || p.title);  // drop empty rows
        if (!payload.length) { toast.error("Add at least one row with a client or title"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/job-flow/jobs/bulk", { jobs: payload });
            toast.success(`${data.created} created · ${data.failed} failed`);
            if (data.failed > 0) {
                // Keep failed rows visible, drop created ones
                const failedInputs = (data.results || []).filter((r) => r.status === "failed");
                if (failedInputs.length) {
                    setRows(failedInputs.map((f) => ({ ...emptyBulkRow(), ...toRow(f.input) })));
                    return;
                }
            }
            onDone();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    }

    function toRow(input) {
        const r = { ...emptyBulkRow(), type: input.type || "income_tax", client_id: input.client_id || "", due_date: input.due_date || "", starting_step_index: input.starting_step_index || 0, starting_owner_user_id: input.starting_owner_user_id || "" };
        if (input.type === "others") r.title = input.title || "";
        if (input.type === "income_tax") r.period_label = input.tax_year || "";
        if (["gst", "payroll"].includes(input.type)) r.period_label = `${input.period_start || ""} → ${input.period_end || ""}`;
        return r;
    }

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-5xl" data-testid="jf-bulk-dialog">
                <DialogHeader><DialogTitle>Add several jobs ({rows.length}/20)</DialogTitle></DialogHeader>
                <div className="overflow-x-auto max-h-[60vh]">
                    <table className="w-full text-[12px]">
                        <thead className="text-left text-[11px] text-[#5E636E] bg-zinc-50 sticky top-0">
                            <tr>
                                <th className="px-2 py-1">Type</th>
                                <th className="px-2 py-1">Client / Title</th>
                                <th className="px-2 py-1">Period / Tax year</th>
                                <th className="px-2 py-1">Due date</th>
                                <th className="px-2 py-1">Step</th>
                                <th className="px-2 py-1">Owner</th>
                                <th />
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r, i) => {
                                const pipeline = pipelines[r.type]?.steps || [];
                                return (
                                    <tr key={i} className="border-t border-[#EEEDE7]">
                                        <td className="px-2 py-1">
                                            <select value={r.type} onChange={(e) => patch(i, { type: e.target.value, starting_step_index: 0 })}
                                                className="h-7 rounded border border-[#E4E4E7] bg-white px-1 text-[12px]" data-testid={`jf-bulk-type-${i}`}>
                                                {Object.entries(JOB_TYPE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                                            </select>
                                        </td>
                                        <td className="px-2 py-1">
                                            {r.type === "others" ? (
                                                <Input value={r.title} onChange={(e) => patch(i, { title: e.target.value })} placeholder="Title (required for Others)" className="h-7 text-[12px]" data-testid={`jf-bulk-title-${i}`} />
                                            ) : (
                                                <select value={r.client_id} onChange={(e) => patch(i, { client_id: e.target.value })}
                                                    className="h-7 w-full rounded border border-[#E4E4E7] bg-white px-1 text-[12px]" data-testid={`jf-bulk-client-${i}`}>
                                                    <option value="">— Pick client —</option>
                                                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                                                </select>
                                            )}
                                        </td>
                                        <td className="px-2 py-1">
                                            {r.type === "income_tax" ? (
                                                <select value={r.period_label || `FY${currentNzFy()}`}
                                                    onChange={(e) => patch(i, { period_label: e.target.value })}
                                                    className="h-7 w-full rounded border border-[#E4E4E7] bg-white px-1 text-[12px]">
                                                    {fyOptions().map((y) => <option key={y} value={y}>{y}</option>)}
                                                </select>
                                            ) : (
                                                <Input value={r.period_label} onChange={(e) => patch(i, { period_label: e.target.value })}
                                                    placeholder={r.type === "others" ? "—" : "dd.mm → dd.mm"}
                                                    disabled={r.type === "others"}
                                                    className="h-7 text-[12px]" />
                                            )}
                                        </td>
                                        <td className="px-2 py-1">
                                            <Input type="date" value={nzToIso(r.due_date)}
                                                onChange={(e) => patch(i, { due_date: isoToNz(e.target.value) })}
                                                className="h-7 text-[12px]" />
                                        </td>
                                        <td className="px-2 py-1">
                                            <select value={r.starting_step_index} onChange={(e) => patch(i, { starting_step_index: Number(e.target.value) })}
                                                className="h-7 rounded border border-[#E4E4E7] bg-white px-1 text-[12px]">
                                                {pipeline.map((s, idx) => <option key={s.key} value={idx}>{idx + 1}. {s.label}</option>)}
                                            </select>
                                        </td>
                                        <td className="px-2 py-1">
                                            <select value={r.starting_owner_user_id} onChange={(e) => patch(i, { starting_owner_user_id: e.target.value })}
                                                className="h-7 rounded border border-[#E4E4E7] bg-white px-1 text-[12px]">
                                                <option value="">— default —</option>
                                                {team.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
                                            </select>
                                        </td>
                                        <td className="px-2 py-1">
                                            <button onClick={() => removeRow(i)} className="text-[#5E636E] hover:text-red-700" title="Remove">
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="pt-2">
                    <Button variant="outline" onClick={addRow} disabled={rows.length >= 20} data-testid="jf-bulk-add-row">
                        <Plus className="h-3.5 w-3.5 mr-1" /> Add row
                    </Button>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Cancel</Button>
                    <Button onClick={submit} disabled={busy} className="bg-[#0F1115] text-white hover:bg-[#2E2E2E]" data-testid="jf-bulk-submit">
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}Create all
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
